import { styled } from '@mui/material/styles'
import {
  Box,
  Checkbox,
  Divider,
  InputBase,
  InputLabel,
  Pagination,
  Paper,
  Rating,
  TableContainer,
  TablePagination,
  TableRow,
} from '@mui/material'
import TableCell, { tableCellClasses } from '@mui/material/TableCell'
import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip'
import { Link } from 'react-router-dom'

export const StyledInput = styled(InputBase)(({ theme }) => ({
  '& .MuiInputBase-input': {
    borderRadius: 0,
    position: 'relative',
    backgroundColor: theme.palette.background.default,
    color: theme.palette.secondary.main,
    fontSize: '14px',
    maxWidth: '140px',
    padding: '0',
    height: '34px',
    transition: theme.transitions.create(['border-color', 'background-color', 'box-shadow']),
    '&:focus': {
      border: 'none',
    },
  },
  '& .MuiInputBase-input.Mui-disabled': {
    '-webkitTextFillColor': `${theme.palette.secondary.main} !important`,
  },
}))

export const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: theme.palette.primary.main,
    fontSize: '18px',
    fontWeight: 400,
    lineHeight: '21px',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: '16px',
    fontWeight: 300,
    lineHeight: '21px',
    color: theme.palette.primary.main,
    borderRight: `1px solid ${theme.palette.primary.main}20`,
    '&:last-child td': {
      border: 0,
    },
  },
  border: 0,
}))

export const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.mixins.greyLight.color,
  },
  border: 0,
  // hide last border
  '&:last-child td, &:last-child th': {
    // border: 0,
  },
}))

export const StyledTablePaper = styled(Paper)(({ theme }) => ({
  boxShadow: 'none',
  background: theme.palette.background.default,
}))

export const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  backgroundColor: 'white',
  overflow: 'hidden',
  '::-webkit-scrollbar': {
    width: '6px',
    background: theme.mixins.scrollBar.color,
    borderRadius: '5px',
    height: '105px',
  },
  '::-webkit-scrollbar-track': {
    boxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
    webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
  },
  '::-webkit-scrollbar-thumb': {
    background: theme.palette.info.main,
    borderRadius: '10px',
    height: 'auto',
  },
}))

export const StyledTablePagination = styled(TablePagination)(({ theme }) => ({
  background: theme.palette.background.default,
  borderBottom: 0,
  display: 'flex',
  justifyContent: 'flex-end',
  marginRight: '48px',
}))

export const StyledContainerAlertErrorBox = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginTop: '15px',
}))

export const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.info.main,
    padding: '8px',
    borderRadius: '5px',
    maxWidth: '160px',
    width: '100%',
    fontSize: '11px',
    fontWeight: 300,
    lineHeight: '13px',
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.info.main,
  },
}))

export const InputApp = styled(InputBase)(({ theme }) => ({
  'label + &': {
    marginTop: theme.spacing(2.4),
  },
  '& .MuiInputBase-input': {
    borderRadius: '5px',
    position: 'relative',
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${theme.palette.info.light}`,
    color: theme.palette.secondary.light,
    fontSize: '12px',
    width: '100%',
    padding: '9.375px 10px',
    transition: theme.transitions.create(['border-color', 'background-color', 'box-shadow']),
    '&:focus': {
      border: `1px solid ${theme.palette.info.light}`,
    },
    '&:disabled': {
      backgroundColor: theme.palette.background.paper,
    },
  },
}))

export const InputLabelApp = styled(InputLabel)(({ theme }) => ({
  '&.MuiInputLabel-root': {
    color: theme.palette.secondary.main,
    fontSize: '16px',
    lineHeight: '18px',
    fontWeight: 400,
  },
}))

export const StyledRating = styled(Rating)(({ theme }) => ({
  '& .MuiRating-iconFilled': {
    color: theme.mixins.rating.color,
  },
  '& .MuiRating-iconHover': {
    color: theme.mixins.rating.color,
  },
  '& .MuiRating-iconEmpty': {
    color: theme.mixins.rating.color,
  },
  '&.MuiRating-root.Mui-disabled': {
    opacity: '0.9',
  },
}))

export const StyledDivider = styled(Divider)(({ theme }) => ({
  borderColor: theme.palette.info.dark,
  height: '30px',
}))

export const StyledPagination = styled(Pagination)(({ theme }) => ({
  '& .MuiButtonBase-root': {
    backgroundColor: theme.palette.background.default,
    borderRadius: '5px',
    border: `1.2px solid ${theme.palette.info.main}`,
    color: theme.palette.info.main,
    fontSize: '12px',
    fontWeight: 500,
  },
  '& .MuiButtonBase-root:hover': {
    backgroundColor: `${theme.palette.info.light} !important`,
    color: theme.palette.background.default,
  },
  '& .MuiButtonBase-root:disabled': {
    backgroundColor: `${theme.palette.background.default} !important`,
    color: theme.palette.secondary.light,
  },
  '& .Mui-selected': {
    backgroundColor: `${theme.palette.info.main} !important`,
    color: theme.palette.background.default,
  },
  '& .Mui-selected:hover': {
    backgroundColor: `${theme.palette.info.light} !important`,
    color: theme.palette.background.default,
  },
}))

export const ContainerApp = styled(Box)(() => ({
  width: '100%',
  maxWidth: '1700px',
  justifyContent: 'space-between',
  alignItems: 'start',
  flexDirection: 'row',
  display: 'flex',
  minHeight: '100vh',
}))

export const ContainerAppWithSidebar = styled(Box)(() => ({
  marginLeft: '320px',
  width: '100%',
}))

export const CheckBox = styled(Checkbox)(({ theme }) => ({
  '& .MuiSvgIcon-root': {
    color: theme.palette.info.light,
  },
}))

export const StyledLink = styled(Link)(({ theme }) => ({
  fontSize: '12px',
  fontWeight: 400,
  color: theme.palette.info.light,
  lineHeight: '12px',
}))
